import { useState, useEffect } from 'react';
import { Box, Typography, Button, Grid, CircularProgress, Alert, Snackbar } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { FileDownload } from '@mui/icons-material';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import StatCard from '../components/rewards/StatCard';
import RewardsTable from '../components/rewards/RewardsTable';
import { getFullUserData, getUserBusinessName } from '../utils/userHelper';
import { RewardClaim } from '../types/reward';
import { getRewardClaims, approveRewardClaim, rejectRewardClaim, RewardClaimResponse } from '../services/rewardsService';

const PageContainer = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  bgcolor: '#F8F9FA'
});

const MainContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
});

const ContentArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '1400px',
  margin: '0 auto',
  width: '100%'
}));

// Helper function to transform API response to UI format
const transformClaimData = (apiClaim: RewardClaimResponse): RewardClaim => {
  return {
    id: apiClaim.id,
    customerName: apiClaim.customer_name,
    customerEmail: apiClaim.customer_email,
    submissionDate: apiClaim.claimed_at || apiClaim.created_at,
    rewardType: apiClaim.reward_type || 'unknown',
    rewardDescription: apiClaim.reward_description || apiClaim.reward_value, // Use reward_description if available, fallback to reward_value
    rewardValue: apiClaim.reward_value,
    status: apiClaim.status as RewardClaim['status'],
    campaignId: apiClaim.campaign_id,
    campaignName: apiClaim.campaign_name,
    claimed: apiClaim.status !== 'pending' && apiClaim.status !== 'not_claimed' // Consider claimed if status is not pending/not_claimed
  };
};

const RewardsManagement = () => {
  const navigate = useNavigate();
  const userData = getFullUserData();
  const businessName = getUserBusinessName();
  
  const [claims, setClaims] = useState<RewardClaim[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch claims on mount
  useEffect(() => {
    fetchClaims();
  }, []);

  const fetchClaims = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await getRewardClaims();

      if (response.success && response.data && response.data.claims) {
        const transformedClaims = response.data.claims.map(transformClaimData);
        setClaims(transformedClaims);
      } else if (response.success && response.data && !response.data.claims) {
        // Backend returned success but no claims (empty list)
        setClaims([]);
      } else {
        throw new Error(response.message || 'Failed to fetch reward claims');
      }
    } catch (err) {
      console.error('Error fetching claims:', err);
      setError(err instanceof Error ? err.message : 'Failed to load reward claims');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (claimId: string) => {
    try {
      setError(null);
      const response = await approveRewardClaim(claimId);
      
      if (response.success) {
        // Update local state
        setClaims(claims.map(claim =>
          claim.id === claimId ? { ...claim, status: 'approved' as const } : claim
        ));
        setSuccess('Reward claim approved successfully!');
      } else {
        throw new Error(response.message || 'Failed to approve claim');
      }
    } catch (err) {
      console.error('Error approving claim:', err);
      setError(err instanceof Error ? err.message : 'Failed to approve claim');
    }
  };

  const handleReject = async (claimId: string) => {
    try {
      setError(null);
      const response = await rejectRewardClaim(claimId);
      
      if (response.success) {
        // Update local state
        setClaims(claims.map(claim =>
          claim.id === claimId ? { ...claim, status: 'rejected' as const } : claim
        ));
        setSuccess('Reward claim rejected successfully!');
      } else {
        throw new Error(response.message || 'Failed to reject claim');
      }
    } catch (err) {
      console.error('Error rejecting claim:', err);
      setError(err instanceof Error ? err.message : 'Failed to reject claim');
    }
  };

  const handleViewDetails = (claimId: string) => {
    console.log('View details for claim:', claimId);
    // TODO: Navigate to claim details page or open modal
  };

  const handleRedeem = async (claimId: string) => {
    try {
      setError(null);
      console.log('Redeem coupon for claim:', claimId);
      // TODO: Implement redeem coupon logic
      // For now, approve the claim
      await handleApprove(claimId);
      setSuccess('Coupon redeemed successfully!');
    } catch (err) {
      console.error('Error redeeming coupon:', err);
      setError(err instanceof Error ? err.message : 'Failed to redeem coupon');
    }
  };

  const handlePayout = async (claimId: string) => {
    try {
      setError(null);
      console.log('Process payout for claim:', claimId);
      // TODO: Implement payout logic
      // For now, approve the claim
      await handleApprove(claimId);
      setSuccess('Payout processed successfully!');
    } catch (err) {
      console.error('Error processing payout:', err);
      setError(err instanceof Error ? err.message : 'Failed to process payout');
    }
  };

  const handleApproveSelected = async (claimIds: string[]) => {
    try {
      setError(null);
      console.log('Approve selected claims:', claimIds);
      
      // Approve all selected claims in parallel
      await Promise.all(claimIds.map(id => approveRewardClaim(id)));
      
      // Update local state
      setClaims(claims.map(claim =>
        claimIds.includes(claim.id) ? { ...claim, status: 'approved' as const } : claim
      ));
      
      setSuccess(`${claimIds.length} reward claim${claimIds.length > 1 ? 's' : ''} approved successfully!`);
    } catch (err) {
      console.error('Error approving selected claims:', err);
      setError(err instanceof Error ? err.message : 'Failed to approve selected claims');
    }
  };

  const handleExportData = () => {
    console.log('Export data');
    // TODO: Implement CSV/Excel export
  };

  return (
    <PageContainer>
      <Sidebar user={userData} />
      
      <MainContent>
        <DashboardHeader
          businessName={businessName}
          userAvatar={userData.avatar}
          businessLogo={userData.businessLogo}
        />
        
        <ContentArea>
          {/* Page Heading */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              mb: 4
            }}
          >
            <Box sx={{ minWidth: 288 }}>
              <Typography
                sx={{
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: 700,
                  fontSize: { xs: '1.875rem', md: '2.25rem' },
                  lineHeight: 1.2,
                  letterSpacing: '-0.02em',
                  color: '#111827',
                  mb: 1
                }}
              >
                Reward Claims Management
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: '1rem',
                  color: 'text.secondary'
                }}
              >
                View and manage all customer reward claims.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={handleExportData}
              sx={{
                minWidth: 84,
                height: 40,
                color: 'text.primary',
                borderColor: '#E5E7EB',
                backgroundColor: '#F9FAFB',
                fontWeight: 700,
                fontSize: '0.875rem',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                  borderColor: '#D1D5DB'
                }
              }}
            >
              Export Data
            </Button>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Pending Claims"
                value={24}
                change={5}
                trend="up"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Total Payouts This Month"
                value="$1,250"
                change={10}
                trend="down"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Available Rewards Budget"
                value="$8,750"
                change={2}
                trend="up"
              />
            </Grid>
          </Grid>

          {/* Rewards Table */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : (
            <RewardsTable
              claims={claims}
              onApprove={handleApprove}
              onReject={handleReject}
              onViewDetails={handleViewDetails}
              onApproveSelected={handleApproveSelected}
              onExportData={handleExportData}
              onRedeem={handleRedeem}
              onPayout={handlePayout}
            />
          )}
        </ContentArea>
      </MainContent>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default RewardsManagement;

